import { type NetworkType, useIntl, type WalletTransactionBatchWithTransactions } from '@capsicum/common';
import { Button, ModalService, Skeleton, TextButton, useRouter } from '@capsicum/common-web';
import { AlertIcon } from '@capsicum/common-web/icons';
import {
	TabNames,
	useUserAuthMethods,
	useWalletTransferOutCost,
	useWalletWithdrawPost,
	useWithdrawDataAtom,
	type WalletSuccessfulWithdrawStatus
} from '@capsicum/features/wallet/shared';
import { useParams } from 'next/navigation';
import { type ReactNode, useEffect, useState } from 'react';

import { ConfirmationErrorModal, ConfirmationModal } from '../../../ui';

import { FormWithoutPasswordInput } from './FormWithoutPasswordInput';
import { FormWithPasswordInput } from './FormWithPasswordInput';
import { FormWithSkeletons } from './FormWithSkeletons';
import { S } from './Password.styled';

export const Password = () => {
	const { back, push } = useRouter();
	const params = useParams();
	const [modalId, setModalId] = useState('');
	const { setInputAmount } = useWithdrawDataAtom();

	const { $t } = useIntl();

	const recipientId = (params?.recipientId || '') as string;

	const { isPending: isWithdrawLoading, mutate: saveWithdraw } = useWalletWithdrawPost();

	const { currencyId, inputAmount: amount, selectedNetworkType: networkType } = useWithdrawDataAtom();

	const { hasAuthSet, isLoading: isLoadingHasAuthSet } = useUserAuthMethods();

	const { data: transferOutCost, isLoading: isLoadingTransferOutCost } = useWalletTransferOutCost({
		amount,
		currencyId,
		withdrawToNetwork: networkType as NetworkType
	});

	const texts = {
		alertText: transferOutCost?.isCHZWithdraw
			? $t('wallet.withdraw.password.alertText')
			: $t('wallet.withdraw.password.alertTextFT'),
		alertTitle: transferOutCost?.isCHZWithdraw
			? $t('wallet.withdraw.password.alertTitle')
			: $t('wallet.withdraw.password.alertTitleFT'),
		cancel: $t('wallet.withdraw.password.buttonCancel'),
		title: $t('wallet.withdraw.password.title')
	};
	/* This function removes the params from cache if user clicks browser back button and
	transfer was successful so the routeGuard can redirect to the wallet if there is no withdrawal input set*/

	useEffect(() => {
		return () => {
			if (modalId) {
				ModalService.removeModal(modalId);
				setInputAmount('');
			}
		};
	}, [modalId]);

	const onTransferSuccess = (data: undefined | WalletTransactionBatchWithTransactions) => {
		setModalId(
			ModalService.addModal({
				isClosable: false,
				renderComponent: () => (
					<ConfirmationModal
						amount={
							transferOutCost?.isCHZWithdraw
								? transferOutCost?.formattedAmountToWithdraw
								: transferOutCost?.formattedTotalAmount
						}
						reference={data?.referenceNumber}
						status={data?.status as WalletSuccessfulWithdrawStatus}
						testID="withdraw-confirmation-modal"
					/>
				),
				renderFooter: ({ close }) => (
					<S.ButtonWrapper>
						<Button
							color="brand"
							fullWidth
							testID="withdraw-confirmation-backWallet"
							text={$t('wallet.withdraw.processingDrawer.walletButton')}
							variant="default"
							onClick={() => {
								push(`/wallet?tab=${TabNames.FAN_TOKENS}`);
								close();
								setInputAmount('');
							}}
						/>
					</S.ButtonWrapper>
				)
			})
		);
	};
	const onTransferError = () => {
		setModalId(
			ModalService.addModal({
				renderComponent: ({ close }) => (
					<ConfirmationErrorModal
						testID="withdraw-confirmation-errorModal"
						onButtonPress={() => {
							close();
						}}
						onSecondaryActionOnButtonPress={() => {
							close();
							push(`/support`);
						}}
					/>
				)
			})
		);
	};

	const handleWithdraw = () =>
		saveWithdraw(
			{
				...transferOutCost?.transferOutCostData,
				externalAddress: {
					id: recipientId
				}
			},
			{
				onError: onTransferError,
				onSuccess: ({ data }) => {
					onTransferSuccess(data);
				}
			}
		);

	let form;
	let title: ReactNode | undefined;

	if (isLoadingHasAuthSet) {
		form = <FormWithSkeletons />;
		title = (
			<Skeleton>
				<S.TitleSkeletonItem width={320} />
			</Skeleton>
		);
	} else {
		const isLoading = isLoadingTransferOutCost || isWithdrawLoading;

		if (hasAuthSet) {
			form = <FormWithPasswordInput isLoading={isLoading} onWithdraw={handleWithdraw} />;
			title = texts.title;
		} else {
			form = <FormWithoutPasswordInput isLoading={isLoading} onWithdraw={handleWithdraw} />;
		}
	}

	return (
		<S.Container>
			{title && (
				<S.Title color="main.enabled" variant="title5">
					{title}
				</S.Title>
			)}
			<S.ConfirmPasswordCard>
				<S.AlertWrapper
					closable={false}
					icon={AlertIcon}
					subTitle={texts.alertText}
					testID="withdrawCHZ-password-warning"
					title={texts.alertTitle}
					type="warning"
				/>
				{form}
				<TextButton
					color="neutral"
					testID="withdraw-password-cancelButton"
					text={texts.cancel}
					onClick={() => back()}
				/>
			</S.ConfirmPasswordCard>
		</S.Container>
	);
};
